/** A visible sub-range of the waterfall's [0, totalMs] time domain. */
export interface ViewWindow {
  startMs: number;
  endMs: number;
}

export interface ScaledBar {
  visible: boolean;
  leftPercent: number;
  widthPercent: number;
  /** Set when the bar is hidden because it sits entirely off one edge of the window. */
  offscreen: "before" | "after" | null;
}

/** Floor so sub-millisecond phases stay visible (matches the original inline math). */
const MIN_BAR_WIDTH_PERCENT = 0.3;

/**
 * Position a bar [startMs, startMs+durationMs] within a view window, as percentages
 * of the window. Bars straddling an edge are clipped; bars fully outside report which
 * edge they fell off (so the UI can hint at off-screen phases). With
 * viewWindow = {0, totalMs} this reproduces the un-zoomed geometry.
 */
export function scaleBar(startMs: number, durationMs: number, viewWindow: ViewWindow): ScaledBar {
  const span = viewWindow.endMs - viewWindow.startMs;
  if (span <= 0) return { visible: false, leftPercent: 0, widthPercent: 0, offscreen: null };

  const barEnd = startMs + Math.max(durationMs, 0);
  if (barEnd < viewWindow.startMs) {
    return { visible: false, leftPercent: 0, widthPercent: 0, offscreen: "before" };
  }
  if (startMs > viewWindow.endMs) {
    return { visible: false, leftPercent: 0, widthPercent: 0, offscreen: "after" };
  }

  const clippedStart = Math.max(startMs, viewWindow.startMs);
  const clippedEnd = Math.min(barEnd, viewWindow.endMs);
  const leftPercent = ((clippedStart - viewWindow.startMs) / span) * 100;
  const widthPercent = Math.max(((clippedEnd - clippedStart) / span) * 100, MIN_BAR_WIDTH_PERCENT);
  return { visible: true, leftPercent, widthPercent, offscreen: null };
}

/**
 * Map a horizontal pixel drag over the timeline track to a new view window, nested
 * inside the current one. Pixels are ordered and clamped to the track, so a
 * right-to-left drag works. Returns null when the resulting span is below minSpanMs
 * (degenerate zoom) or the track has no width.
 */
export function zoomWindowFromPixels(
  pixelStart: number,
  pixelEnd: number,
  trackWidthPx: number,
  current: ViewWindow,
  minSpanMs: number,
): ViewWindow | null {
  if (trackWidthPx <= 0) return null;
  const span = current.endMs - current.startMs;
  if (span <= 0) return null;

  const low = Math.max(0, Math.min(pixelStart, pixelEnd));
  const high = Math.min(trackWidthPx, Math.max(pixelStart, pixelEnd));
  const startMs = current.startMs + (low / trackWidthPx) * span;
  const endMs = current.startMs + (high / trackWidthPx) * span;
  if (endMs - startMs < minSpanMs) return null;
  return { startMs, endMs };
}

/** Whether a window is narrower than the full [0, totalMs] domain. */
export function isZoomed(viewWindow: ViewWindow, totalMs: number): boolean {
  return viewWindow.startMs > 0 || viewWindow.endMs < totalMs;
}
